import type { InviteDto } from '@lagerfeuer/shared';
import { Check, Copy } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import { errorMessage } from '../../lib/api';
import { invitesApi } from '../../lib/endpoints';
import { inviteUrl } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { can, channelPermissions, groupServerChannels } from '../../store/selectors';
import { describeExpiry, INVITE_AGES, INVITE_USES } from './inviteCode';
import './servers.css';

/** In die Zwischenablage kopieren; liefert true bei Erfolg. */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Einladung erstellen und teilen: Kanal, Ablauf und max. Nutzungen wählbar, Vanity-URL wird angezeigt. */
export function InviteDialog({ serverId, channelId, onClose }: { serverId: string; channelId?: string; onClose: () => void }) {
  const server = useAppStore((s) => s.servers[serverId]);
  const channels = useAppStore((s) => s.channels);
  const roles = useAppStore((s) => s.roles[serverId]);
  const invitable = useMemo(() => {
    const st = useAppStore.getState();
    return groupServerChannels(st, serverId)
      .flatMap((g) => g.channels)
      .filter((c) => c.type !== 'CATEGORY' && can(channelPermissions(st, c.id), 'CREATE_INVITE'));
  }, [serverId, channels, roles]); // eslint-disable-line react-hooks/exhaustive-deps
  const initialChannel = (channelId && invitable.some((c) => c.id === channelId) ? channelId : server?.systemChannelId && invitable.some((c) => c.id === server.systemChannelId) ? server.systemChannelId : invitable[0]?.id) ?? '';
  const [target, setTarget] = useState(initialChannel);
  const [maxAge, setMaxAge] = useState(604800);
  const [maxUses, setMaxUses] = useState(0);
  const [invite, setInvite] = useState<InviteDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const started = useRef(false);

  const create = useCallback(
    async (ch: string, age: number, uses: number) => {
      if (!ch) return;
      setBusy(true);
      setError(null);
      try {
        setInvite(await invitesApi.create(ch, { maxAgeSeconds: age, maxUses: uses }));
        setEditing(false);
      } catch (e) {
        setError(errorMessage(e));
      } finally {
        setBusy(false);
      }
    },
    [],
  );

  // Beim Öffnen direkt einen Link mit Standardwerten erzeugen (7 Tage, unbegrenzt).
  useEffect(() => {
    if (started.current || !initialChannel) return;
    started.current = true;
    void create(initialChannel, 604800, 0);
  }, [create, initialChannel]);

  const copy = async (text: string, key: string) => {
    if (await copyText(text)) {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 2000);
    }
  };

  if (!server) return null;
  const link = invite ? inviteUrl(invite.code) : '';

  return (
    <Modal title={`Freunde zu „${server.name}" einladen`} onClose={onClose} testId="invite-dialog">
      <div className="stack">
        {invitable.length === 0 ? (
          <p className="muted" data-testid="invite-no-channel">
            Du darfst in keinem Kanal dieses Servers Einladungen erstellen.
          </p>
        ) : (
          <>
            <div className="field">
              <label htmlFor="invite-link">Einladungslink</label>
              <div className="copy-row">
                <input id="invite-link" className="input" readOnly value={busy && !invite ? 'Wird erstellt …' : link} onFocus={(e) => e.currentTarget.select()} data-testid="invite-link" />
                <button className="btn btn-primary" onClick={() => void copy(link, 'link')} disabled={!invite} data-testid="invite-copy">
                  {copied === 'link' ? <Check size={16} /> : <Copy size={16} />}
                  {copied === 'link' ? 'Kopiert' : 'Kopieren'}
                </button>
              </div>
              {invite && (
                <span className="field-hint" data-testid="invite-meta">
                  Link {describeExpiry(invite.expiresAt)}
                  {invite.maxUses > 0 ? ` · höchstens ${invite.maxUses} Nutzungen` : ''}.{' '}
                  {!editing && (
                    <button className="btn-link" onClick={() => setEditing(true)} data-testid="invite-edit">
                      Link bearbeiten
                    </button>
                  )}
                </span>
              )}
            </div>
            {(editing || (!invite && !busy)) && (
              <div className="stack invite-options" data-testid="invite-options">
                <div className="field">
                  <label htmlFor="invite-channel">Kanal</label>
                  <select id="invite-channel" className="select" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="invite-channel">
                    {invitable.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.type === 'VOICE' ? '🔊 ' : '# '}
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="row" style={{ flexWrap: 'wrap', alignItems: 'flex-start' }}>
                  <div className="field" style={{ flex: 1, minWidth: 160 }}>
                    <label htmlFor="invite-age">Läuft ab nach</label>
                    <select id="invite-age" className="select" value={maxAge} onChange={(e) => setMaxAge(Number(e.target.value))} data-testid="invite-max-age">
                      {INVITE_AGES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="field" style={{ flex: 1, minWidth: 160 }}>
                    <label htmlFor="invite-uses">Maximale Nutzungen</label>
                    <select id="invite-uses" className="select" value={maxUses} onChange={(e) => setMaxUses(Number(e.target.value))} data-testid="invite-max-uses">
                      {INVITE_USES.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="row" style={{ justifyContent: 'flex-end' }}>
                  {invite && (
                    <button className="btn btn-ghost" onClick={() => setEditing(false)}>
                      Abbrechen
                    </button>
                  )}
                  <button className="btn btn-primary" onClick={() => void create(target, maxAge, maxUses)} disabled={busy || !target} data-testid="invite-create">
                    Neuen Link erstellen
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {server.vanityCode && (
          <div className="field" data-testid="invite-vanity">
            <label htmlFor="invite-vanity">Vanity-URL des Servers</label>
            <div className="copy-row">
              <input id="invite-vanity" className="input" readOnly value={inviteUrl(server.vanityCode)} onFocus={(e) => e.currentTarget.select()} data-testid="invite-vanity-link" />
              <button className="btn" onClick={() => void copy(inviteUrl(server.vanityCode!), 'vanity')} data-testid="invite-vanity-copy">
                {copied === 'vanity' ? <Check size={16} /> : <Copy size={16} />}
                {copied === 'vanity' ? 'Kopiert' : 'Kopieren'}
              </button>
            </div>
          </div>
        )}
        {error && (
          <p className="error-text" role="alert" data-testid="invite-error">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
